import { useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, CalendarClock, Gift, Pin, Sparkles, Workflow } from "lucide-react";

export interface DayItem { kind: "holiday" | "season" | "deadline" | "overdue" | "auto"; text: string; slug?: string }
export interface Day { day: string; pins: number; pins_posted: number; items: DayItem[] }

const ICON = { holiday: Gift, season: Sparkles, deadline: CalendarClock, overdue: AlertTriangle, auto: Workflow };
const TONE = { holiday: "#F9C6D3", season: "#D2EDFF", deadline: "#F6D776", overdue: "#FDE6DF", auto: "#E2F5E0" };

const fmt = (iso: string, opts: Intl.DateTimeFormatOptions) => new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", opts);

function DayDetail({ d }: { d: Day }) {
  return (
    <div className="mt-4 rounded-2xl border-2 border-[#23324A]/10 bg-white p-5" data-testid="week-day-detail">
      <p className="font-display text-lg font-black text-[#23324A]">{fmt(d.day, { weekday: "long", month: "long", day: "numeric" })}</p>
      <ul className="mt-3 space-y-2">
        <li className="flex items-center gap-3 text-sm text-[#23324A]">
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-[#BFE0F5]"><Pin className="h-4 w-4" strokeWidth={2.5} /></span>
          {d.pins ? <Link to="/admin/pins" className="font-semibold hover:underline">{d.pins} pin{d.pins > 1 ? "s" : ""} scheduled{d.pins_posted ? `, ${d.pins_posted} posted` : ""}</Link> : <span className="text-[#23324A]/60">No pins scheduled</span>}
        </li>
        {d.items.map((it, i) => {
          const Icon = ICON[it.kind];
          const body = <span className="font-semibold">{it.text}</span>;
          return (
            <li key={i} className="flex items-center gap-3 text-sm text-[#23324A]" data-testid={`week-item-${it.kind}`}>
              <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg" style={{ background: TONE[it.kind] }}><Icon className="h-4 w-4" strokeWidth={2.5} /></span>
              {it.slug ? <Link to={`/admin/articles?open=${it.slug}`} className="hover:underline">{body}</Link> : it.kind === "overdue" ? <Link to="/admin/pins" className="hover:underline">{body}</Link> : body}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function WeekStrip({ week }: { week: Day[] }) {
  const [sel, setSel] = useState(0);
  return (
    <section className="calm-card p-5 sm:p-6" data-testid="week-strip">
      <h2 className="font-display text-xl font-black text-[#23324A]">This week</h2>
      <div className="mt-4 grid grid-cols-7 gap-1.5 sm:gap-2">
        {week.map((d, i) => (
          <button key={d.day} onClick={() => setSel(i)} data-testid={`week-day-${i}`}
            className={`flex flex-col items-center rounded-2xl border-2 px-1 py-2.5 transition-colors duration-150 ${sel === i ? "border-[#23324A] bg-[#F6D776]" : "border-transparent bg-[#FDFBF7] hover:border-[#23324A]/30"}`}>
            <span className="text-[10px] font-bold uppercase tracking-wider text-[#23324A]/60">{i === 0 ? "Today" : fmt(d.day, { weekday: "short" })}</span>
            <span className="font-display text-xl font-black text-[#23324A]">{fmt(d.day, { day: "numeric" })}</span>
            <span className="mt-1 flex h-2 gap-0.5">
              {d.pins > 0 && <span className="h-2 w-2 rounded-full bg-[#2F6FB5]" />}
              {d.items.slice(0, 3).map((it, j) => <span key={j} className="h-2 w-2 rounded-full" style={{ background: it.kind === "overdue" ? "#E84855" : it.kind === "deadline" ? "#E0A800" : "#F08A6C" }} />)}
            </span>
          </button>
        ))}
      </div>
      <DayDetail d={week[sel]} />
    </section>
  );
}
